import React, { useState } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, ToggleButton,
  ToggleButtonGroup, FormControlLabel, Switch,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parse, isValid, startOfMonth, endOfMonth, subMonths, subDays } from 'date-fns';
import { DashboardFilters } from './types';

type Preset = 'thisMonth' | 'lastMonth' | 'last90' | 'all' | 'custom';

interface FiltersProps {
  filters: DashboardFilters;
  onChange: (filters: DashboardFilters) => void;
  charts: string[];
  languages: string[];
  includeReruns: boolean;
  onIncludeRerunsChange: (v: boolean) => void;
  dateBasis: 'chartWeek' | 'rendered';
  onDateBasisChange: (v: 'chartWeek' | 'rendered') => void;
}

const toIso = (d: Date) => format(d, 'yyyy-MM-dd');
const fromIso = (s: string | null): Date | null => {
  if (!s) return null;
  const d = parse(s, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
};

function presetRange(preset: Preset): { from: string | null; to: string | null } {
  const now = new Date();
  switch (preset) {
    case 'thisMonth':
      return { from: toIso(startOfMonth(now)), to: toIso(endOfMonth(now)) };
    case 'lastMonth': {
      const lm = subMonths(now, 1);
      return { from: toIso(startOfMonth(lm)), to: toIso(endOfMonth(lm)) };
    }
    case 'last90':
      return { from: toIso(subDays(now, 90)), to: toIso(now) };
    default:
      return { from: null, to: null };
  }
}

export const Filters: React.FC<FiltersProps> = ({
  filters, onChange, charts, languages, includeReruns, onIncludeRerunsChange,
  dateBasis, onDateBasisChange,
}) => {
  const [preset, setPreset] = useState<Preset>('all');

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      onChange({ ...filters, ...presetRange(p) });
    }
  };

  const setCustomDate = (key: 'from' | 'to', d: Date | null) => {
    setPreset('custom');
    onChange({ ...filters, [key]: d && isValid(d) ? toIso(d) : null });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {/* Billing is per chart week, so "Chart date" is the default basis;
          "Run date" filters by when the render actually happened. */}
      <ToggleButtonGroup
        size="small"
        exclusive
        color="primary"
        value={dateBasis}
        onChange={(_e, v) => { if (v) onDateBasisChange(v); }}
      >
        <ToggleButton value="chartWeek">Chart date</ToggleButton>
        <ToggleButton value="rendered">Run date</ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={preset}
        onChange={(_e, v) => { if (v) applyPreset(v); }}
      >
        <ToggleButton value="thisMonth">This month</ToggleButton>
        <ToggleButton value="lastMonth">Last month</ToggleButton>
        <ToggleButton value="last90">Last 90 days</ToggleButton>
        <ToggleButton value="all">All time</ToggleButton>
      </ToggleButtonGroup>

      <DatePicker
        label="From"
        value={fromIso(filters.from)}
        onChange={(d) => setCustomDate('from', d)}
        slotProps={{ textField: { size: 'small', sx: { width: 165 } }, field: { clearable: true } }}
      />
      <DatePicker
        label="To"
        value={fromIso(filters.to)}
        onChange={(d) => setCustomDate('to', d)}
        slotProps={{ textField: { size: 'small', sx: { width: 165 } }, field: { clearable: true } }}
      />

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Chart</InputLabel>
        <Select
          label="Chart"
          value={filters.chart || ''}
          onChange={(e) => onChange({ ...filters, chart: e.target.value || null })}
        >
          <MenuItem value="">All charts</MenuItem>
          {charts.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>Orientation</InputLabel>
        <Select
          label="Orientation"
          value={filters.orientation || ''}
          onChange={(e) => onChange({ ...filters, orientation: e.target.value || null })}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Horizontal">Horizontal</MenuItem>
          <MenuItem value="Vertical">Vertical</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Language</InputLabel>
        <Select
          label="Language"
          value={filters.language || ''}
          onChange={(e) => onChange({ ...filters, language: e.target.value || null })}
        >
          <MenuItem value="">All</MenuItem>
          {languages.map((l) => <MenuItem key={l} value={l}>{l.toUpperCase()}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={includeReruns}
            onChange={(e) => onIncludeRerunsChange(e.target.checked)}
          />
        }
        label="Show reruns"
      />
    </Box>
  );
};
